/**
 * Crypto Fear & Greed Index service.
 * Data by alternative.me — https://alternative.me/crypto/fear-and-greed-index/
 */

export interface FearGreedData {
  value: number;
  classification: string;
  timestamp: number;
}

interface ApiResponse {
  data: { value: string; value_classification: string; timestamp: string }[];
  metadata: { error: string | null };
}

let cache: { data: FearGreedData[]; fetchedAt: number } | null = null;
const CACHE_TTL = 15 * 60 * 1000; // 15 minutes

export async function fetchFearGreedIndex(limit = 30): Promise<FearGreedData[]> {
  if (cache && Date.now() - cache.fetchedAt < CACHE_TTL) {
    return cache.data;
  }

  const res = await fetch(`https://api.alternative.me/fng/?limit=${limit}&format=json`);
  if (!res.ok) throw new Error(`Fear & Greed API error: ${res.status}`);

  const json: ApiResponse = await res.json();
  if (json.metadata.error) throw new Error(json.metadata.error);

  const data = json.data.map(d => ({
    value: parseInt(d.value, 10),
    classification: d.value_classification,
    timestamp: parseInt(d.timestamp, 10) * 1000,
  }));

  cache = { data, fetchedAt: Date.now() };
  return data;
}

export function getSentimentColor(value: number, darkMode: boolean): string {
  if (value <= 25) return darkMode ? 'text-red-400' : 'text-red-600';
  if (value <= 45) return darkMode ? 'text-orange-400' : 'text-orange-600';
  if (value <= 55) return darkMode ? 'text-yellow-400' : 'text-yellow-600';
  if (value <= 75) return darkMode ? 'text-green-400' : 'text-green-600';
  return darkMode ? 'text-emerald-400' : 'text-emerald-600';
}

export function getSentimentBg(value: number, darkMode: boolean): string {
  if (value <= 25) return darkMode ? 'bg-red-500' : 'bg-red-500';
  if (value <= 45) return darkMode ? 'bg-orange-500' : 'bg-orange-500';
  if (value <= 55) return darkMode ? 'bg-yellow-500' : 'bg-yellow-500';
  if (value <= 75) return darkMode ? 'bg-green-500' : 'bg-green-500';
  return darkMode ? 'bg-emerald-500' : 'bg-emerald-500';
}
